import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Newspaper, RefreshCw } from "lucide-react";
import { ArticleCard } from "@/components/public/ArticleCard";
import { useI18n } from "@/lib/i18n";
import { useRubriques } from "@/hooks/useRubriques";
import { useCategories } from "@/hooks/useCategories";
import { useArticles } from "@/hooks/useArticles";
import { adaptArticles } from "@/lib/adapters";
import { useSeo, useJsonLd } from "@/hooks/useSeo";
import { SITE_URL, truncate } from "@/lib/seo";
import type { RubriqueDto } from "@/types/rubrique.types";
import type { CategorieDto } from "@/types/categorie.types";

function ArticleCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      <div className="aspect-[16/9] rounded-md bg-muted" />
      <div className="h-3 w-16 rounded bg-muted" />
      <div className="space-y-1.5">
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 w-3/4 rounded bg-muted" />
      </div>
      <div className="h-3 w-1/2 rounded bg-muted" />
    </div>
  );
}

function RubriqueDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { locale, t } = useI18n();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  const { data: rubriquesData, isLoading: loadingRubriques } = useRubriques();
  const rubriques: RubriqueDto[] = Array.isArray(rubriquesData) ? rubriquesData : [];
  const rubrique = rubriques.find((r) => r.slug === slug);

  const { data: categoriesData, isLoading: loadingCategories } = useCategories();
  const categories: CategorieDto[] = Array.isArray(categoriesData) ? categoriesData : [];

  const {
    data: articlesData,
    isLoading: loadingArticles,
    isError: errorArticles,
    refetch: refetchArticles,
  } = useArticles(0, 100, "PUBLIE", { rubriqueId: rubrique?.id });

  const rawDtos = Array.isArray(articlesData) ? articlesData : (articlesData as any)?.content ?? [];
  const filteredDtos = selectedCategoryId
    ? rawDtos.filter((a: any) => a.categorieIds?.includes(selectedCategoryId))
    : rawDtos;
  const items = adaptArticles(filteredDtos);

  const isLoading = loadingRubriques || loadingCategories || loadingArticles;

  const rubriqueLabel = rubrique ? (locale === "fr" ? rubrique.nom : (rubrique.nomEn || rubrique.nom)) : slug ?? "";

  useSeo({
    title: `${rubriqueLabel} — Ramba News`,
    description: truncate(
      locale === "fr"
        ? `Toute l'actualité de la rubrique ${rubriqueLabel} sur Ramba News.`
        : `All the news in the ${rubriqueLabel} section on Ramba News.`
    ),
    path: `/rubrique/${slug}`,
  });

  useJsonLd("ld-breadcrumb", rubrique ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: locale === "fr" ? "Accueil" : "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: rubriqueLabel, item: `${SITE_URL}/rubrique/${slug}` },
    ],
  } : undefined);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 md:py-14">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-verified">{t("rubrique.eyebrow")}</div>
      <h1 className="mt-2 font-serif text-3xl md:text-5xl font-semibold tracking-tight">
        {loadingRubriques ? (
          <span className="inline-block h-9 w-48 rounded bg-muted animate-pulse" />
        ) : rubrique ? (
          locale === "fr" ? rubrique.nom : (rubrique.nomEn || rubrique.nom)
        ) : (
          slug
        )}
      </h1>

      <div className="mt-8 grid gap-8 lg:grid-cols-[220px_1fr]">
        {/* Sidebar catégories */}
        <aside className="space-y-1">
          <button
            type="button"
            onClick={() => setSelectedCategoryId(null)}
            className={`block w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
              selectedCategoryId === null
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:bg-surface hover:text-foreground"
            }`}
          >
            {t("rubrique.sidebar.all")}
          </button>
          {loadingCategories ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-8 w-full rounded-md bg-muted animate-pulse" />
            ))
          ) : (
            categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedCategoryId(c.id)}
                className={`block w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  selectedCategoryId === c.id
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-surface hover:text-foreground"
                }`}
              >
                {locale === "fr" ? c.libelleFr : c.libelleEn}
              </button>
            ))
          )}
        </aside>

        {/* Liste des articles */}
        <div className="grid gap-8 md:grid-cols-2">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <ArticleCardSkeleton key={i} />)
          ) : errorArticles ? (
            <div className="col-span-full flex flex-col items-center justify-center py-12 gap-3">
              <p className="text-sm text-muted-foreground">{t("error.load")}</p>
              <button
                onClick={() => refetchArticles()}
                className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-semibold hover:bg-surface transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" /> {t("cta.retry")}
              </button>
            </div>
          ) : items.length > 0 ? (
            items.map((a) => <ArticleCard key={a.id} article={a} />)
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
              <Newspaper className="h-8 w-8 opacity-25" />
              <p className="text-sm">{t("category.empty")}</p>
            </div>
          )}
        </div>
      </div>

      <Link to="/" className="mt-10 inline-block text-xs text-muted-foreground hover:text-foreground">
        ← {t("nav.home")}
      </Link>
    </div>
  );
}

export default RubriqueDetailPage;
